import { EARN_EVENTS_BY_CATEGORY } from '../../earnEventsCatalog';
import { EarnReferralSummary } from '../components/EarnReferralSummary';
import type { EarnFilter } from '../rewardsPageTypes';
import { filterEarnList } from '../rewardsPageUtils';
import { getDisplayCategoryName } from '../../categoryNameMapping';
import type { Offer, PointCategoryName, PointTaskCode, PointTasksStats } from '../../../../helpers/utils/types';
import { useAuth } from '../../../../auth/Auth';
import { useCallback, useEffect, useState } from 'react';
import { getTaskPointsText, getTaskTicketsText } from '../../../../helpers/utils/rewardLabels';
import { usePointTasksByCategory } from '../hooks/usePointTasksByCategory';
import { getNewsletterSubscription, subscribeToNewsletter } from '../../../../auth/_requests';
import { usePoints } from '../../../../helpers/hooks/pointsContext';
import { showToast } from '../../../../helpers/utils/showToast';
import { useModal } from '../../../../helpers/hooks/modalContext';
import { useNavigate } from 'react-router-dom';
import { NewsletterSignupModal } from '../components/NewsletterSignupModal';

const isPointCategoryName = (v: string): v is PointCategoryName =>
  [
    'New User Welcome Bonuses',
    'Financial Health & Punctuality',
    'Deep Engagement & Stickiness',
    'Community & Social Growth',
    'Milestone & Seasonal Events',
  ].includes(v);

const NEWSLETTER_TASK_CODE: PointTaskCode = 'newsletterSignup';
const NEWSLETTER_SHELL_ID = 'newsletterSignup';
const IN_APP_REVIEW_TASK_CODE: PointTaskCode = 'inAppReview';
const IN_APP_REVIEW_MODAL_ID = 'in-app-review';
const SOCIAL_TASK_CODES: readonly string[] = ['socialShoutOut', 'socialFollow'];
const SOCIAL_SUBMISSIONS_PATH = '/rewards/social-submissions';

const isOfferCompleted = (t: Offer) => {
  if (t.isCompleted) return true;
  if (t.maxCountPerUser === 1 && t.completedCount >= 1) return true;
  return false;
};

const getCategoryShell = (
  catIdx: number,
  completed: number,
  total: number,
  isOpen: boolean
) => {
  const isComplete = total > 0 && completed >= total;
  const hasProgress = completed > 0 && !isComplete;
  const catClasses = ['en-cat'];
  if (isOpen) catClasses.push('open');
  if (isComplete) catClasses.push('is-complete');
  const pillClasses = ['en-cat-prog-pill'];
  if (isComplete) pillClasses.push('is-complete');
  else if (hasProgress) pillClasses.push('has-progress');
  return {
    dataC: String((catIdx % 5) + 1),
    className: catClasses.join(' '),
    pillClassName: pillClasses.join(' '),
    progressPct: total > 0 ? Math.round((completed / total) * 100) : 0,
  };
};

export type EarnSectionProps = {
  earnFilter: EarnFilter;
  setEarnFilter: (v: EarnFilter) => void;
  earnExpandedCat: string | null;
  setEarnExpandedCat: (v: string | null) => void;
  pointTasks?: Offer[];
  pointStats?: PointTasksStats;
  pointTasksLoading?: boolean;
};

export const EarnSection = (props: EarnSectionProps) => {
  const {
    earnFilter,
    setEarnFilter,
    earnExpandedCat,
    setEarnExpandedCat,
    pointStats,
    pointTasksLoading,
  } = props;

  const apiCategories = pointStats?.categories ?? [];
  const hasApiData = apiCategories.length > 0;
  const { auth } = useAuth();
  const { refreshPoints } = usePoints();
  const { setModal } = useModal();
  const navigate = useNavigate();
  const { tasksByCategory, loadingByCategory, fetchCategory } = usePointTasksByCategory({
    token: auth?.data.token,
    enabled: true,
  });

  const [newsletterSubscribedOn, setNewsletterSubscribedOn] = useState<string | null>(null);
  const [newsletterStatusLoading, setNewsletterStatusLoading] = useState(false);
  const [newsletterModalOpen, setNewsletterModalOpen] = useState(false);
  const [newsletterSubmitting, setNewsletterSubmitting] = useState(false);
  const [newsletterError, setNewsletterError] = useState<string | null>(null);

  const loadNewsletterStatus = useCallback(async () => {
    if (!auth?.data.token) {
      setNewsletterSubscribedOn(null);
      return;
    }
    setNewsletterStatusLoading(true);
    try {
      const sub = await getNewsletterSubscription(auth.data.token);
      setNewsletterSubscribedOn(sub);
    } catch {
      setNewsletterSubscribedOn(null);
    } finally {
      setNewsletterStatusLoading(false);
    }
  }, [auth?.data.token]);

  useEffect(() => {
    void loadNewsletterStatus();
  }, [loadNewsletterStatus]);

  const runAfterNewsletterSubscribe = useCallback(async () => {
    await refreshPoints();
    await loadNewsletterStatus();
  }, [refreshPoints, loadNewsletterStatus]);

  const handleNewsletterSubscribe = useCallback(async () => {
    if (!auth?.data.token) return;
    setNewsletterSubmitting(true);
    setNewsletterError(null);
    try {
      const res = await subscribeToNewsletter(auth.data.token);
      if (res.message !== 'success') {
        throw new Error('Could not subscribe right now. Please try again.');
      }
      await runAfterNewsletterSubscribe();
      setNewsletterModalOpen(false);
      showToast({ message: 'You are subscribed to The Ledger Weekly.', type: 'success' });
    } catch (e) {
      setNewsletterError(e instanceof Error ? e.message : 'Something went wrong.');
    } finally {
      setNewsletterSubmitting(false);
    }
  }, [auth?.data.token, runAfterNewsletterSubscribe]);

  useEffect(() => {
    const first = apiCategories[0]?.pointCategoryName as PointCategoryName | undefined;
    if (!first) return;
    const effectiveExpanded =
      earnExpandedCat && isPointCategoryName(earnExpandedCat) ? earnExpandedCat : first;
    if (!tasksByCategory[effectiveExpanded]) {
      fetchCategory(effectiveExpanded).catch(() => {});
    }
  }, [apiCategories, earnExpandedCat, tasksByCategory, fetchCategory]);

  return (
    <div className='en-panel en-panel-howto is-active'>
      <div className='en-info'>
        <div className='en-info-card blue'>
          <div className='en-info-top'>
            <div className='en-info-ic' aria-hidden='true'>
              <svg
                width='18'
                height='18'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <polygon points='12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2' />
              </svg>
            </div>
            <h3 className='en-info-title'>Points</h3>
          </div>
          <div className='en-info-desc'>
            Redeem for rent savings and gift cards.{' '}
            <strong>Points expire after one year of account inactivity.</strong>
          </div>
          <div className='en-info-tags'>
            <span className='en-info-tag'>115+ brands</span>
            <span className='en-info-tag'>Rent savings</span>
          </div>
        </div>
        <div className='en-info-card gold'>
          <div className='en-info-top'>
            <div className='en-info-ic' aria-hidden='true'>
              <svg
                width='18'
                height='18'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M2 9V6a2 2 0 012-2h16a2 2 0 012 2v3a2 2 0 000 4v3a2 2 0 01-2 2H4a2 2 0 01-2-2v-3a2 2 0 000-4z' />
                <path d='M13 5v2M13 17v2M13 11v2' />
              </svg>
            </div>
            <h3 className='en-info-title'>Tickets</h3>
          </div>
          <div className='en-info-desc'>
            Your tickets into the monthly rent drop.{' '}
            <strong>Reset to zero after each drop closes.</strong>
          </div>
          <div className='en-info-tags'>
            <span className='en-info-tag'>Monthly rent drop</span>
            <span className='en-info-tag'>Resets each drop</span>
          </div>
        </div>
      </div>

      <div className='en-filter'>
        {(
          [
            ['all', 'All events'],
            ['available', 'Available'],
            ['completed', 'Completed'],
          ] as const
        ).map(([val, label]) => (
          <button
            key={val}
            type='button'
            onClick={() => setEarnFilter(val)}
            className={`en-fchip${earnFilter === val ? ' on' : ''}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className='en-howto-split' id='enHowtoSplit'>
        <div className='en-howto-split-l'>
          <div className='en-cats-list'>
        {hasApiData
          ? apiCategories.map((catStat, catIdx) => {
              const cat = catStat.pointCategoryName;
              const allTasks = tasksByCategory[cat] ?? [];
              const headerCompleted = catStat.totalCompleted ?? 0;
              const headerTotal = catStat.totalTasks ?? 0;

              const filteredTasks =
                earnFilter === 'all'
                  ? allTasks
                  : earnFilter === 'completed'
                    ? allTasks.filter(isOfferCompleted)
                    : allTasks.filter((t) => !isOfferCompleted(t));

              const isOpen = earnExpandedCat === cat;
              const numLabel = String(catIdx + 1).padStart(2, '0');
              const shell = getCategoryShell(catIdx, headerCompleted, headerTotal, isOpen);

              return (
                <div
                  key={cat}
                  data-c={shell.dataC}
                  className={shell.className}
                  role='button'
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={async (e) => {
                    if ((e.target as HTMLElement).closest('.en-task')) return;
                    if (isOpen) {
                      setEarnExpandedCat(null);
                      return;
                    }
                    setEarnExpandedCat(cat);
                    if (!tasksByCategory[cat]) {
                      await fetchCategory(cat).catch(() => {});
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      (e.currentTarget as HTMLDivElement).click();
                    }
                  }}
                >
                  <div className='en-cat-hdr'>
                    <div className='en-cat-hdr-l'>
                      <span className='en-cat-num'>{numLabel}</span>
                      <div className='en-cat-title-row'>
                        <span className='en-cat-tt'>{getDisplayCategoryName(cat)}</span>
                        <span className={shell.pillClassName}>
                          {headerCompleted}/{headerTotal} completed
                        </span>
                      </div>
                    </div>
                    <div className='en-cat-hdr-r'>
                      <div className='en-cat-bar' aria-hidden='true'>
                        <div
                          className='en-cat-bar-fill'
                          style={{ width: `${shell.progressPct}%` }}
                        />
                      </div>
                      <span className='en-cat-toggle' aria-hidden='true'>
                        +
                      </span>
                    </div>
                  </div>

                  {isOpen && (
                    <div className='en-cat-body'>
                      {pointTasksLoading || loadingByCategory[cat] ? (
                        <div className='en-empty'>Loading events…</div>
                      ) : filteredTasks.length === 0 ? (
                        <div className='en-empty'>
                          {earnFilter === 'all'
                            ? 'No events in this category.'
                            : `No ${earnFilter} events in this category.`}
                        </div>
                      ) : (
                        filteredTasks.map((task) => {
                          const completed = isOfferCompleted(task);
                          const ptsLabel = getTaskPointsText(task);
                          const tixLabel = getTaskTicketsText(task);

                          const isNewsletter = task.code === NEWSLETTER_TASK_CODE;
                          const newsletterLocked =
                            isNewsletter && (completed || Boolean(newsletterSubscribedOn));
                          const newsletterClickable =
                            isNewsletter && !newsletterLocked && !newsletterStatusLoading;
                          const inAppReviewClickable = task.code === IN_APP_REVIEW_TASK_CODE && !completed;
                          const socialClickable = SOCIAL_TASK_CODES.includes(task.code);
                          const taskClickable = newsletterClickable || inAppReviewClickable || socialClickable;
                          const openTask = () => {
                            if (socialClickable) {
                              navigate(SOCIAL_SUBMISSIONS_PATH);
                              return;
                            }
                            if (inAppReviewClickable) {
                              setModal(IN_APP_REVIEW_MODAL_ID);
                              return;
                            }
                            if (!newsletterClickable) return;
                            setNewsletterError(null);
                            setNewsletterModalOpen(true);
                          };

                          const typeLine = (task.displayLimit ?? '').trim();

                          return (
                            <div
                              key={task.pointTaskId}
                              role={taskClickable ? 'button' : undefined}
                              tabIndex={taskClickable ? 0 : undefined}
                              onClick={(e) => {
                                e.stopPropagation();
                                openTask();
                              }}
                              onKeyDown={
                                taskClickable
                                  ? (e) => {
                                      e.stopPropagation();
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openTask();
                                      }
                                    }
                                  : undefined
                              }
                              className={`en-task${completed ? ' is-done' : ''}${
                                taskClickable ? ' cursor-pointer' : ''
                              }`}
                            >
                              <div className='en-task-l'>
                                <span className='en-task-dot' aria-hidden='true' />
                                <div className='en-task-info'>
                                  <div className='en-task-title'>{task.description}</div>
                                  {typeLine.length > 0 ? (
                                    <span className='en-task-freq'>{typeLine}</span>
                                  ) : null}
                                </div>
                              </div>
                              <div className='en-task-chips'>
                                {ptsLabel ? (
                                  <span className='en-chip-pill pts'>{ptsLabel}</span>
                                ) : null}
                                {tixLabel ? (
                                  <span className='en-chip-pill tix'>{tixLabel}</span>
                                ) : null}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })
          : Object.entries(EARN_EVENTS_BY_CATEGORY).map(([cat, events], catIdx) => {
              const filtered = filterEarnList(events, earnFilter);
              const isOpen = earnExpandedCat === cat;
              const completed = events.filter((e) => e.completed).length;
              const total = events.length;
              const numLabel = String(catIdx + 1).padStart(2, '0');
              const shell = getCategoryShell(catIdx, completed, total, isOpen);

              return (
                <div
                  key={cat}
                  data-c={shell.dataC}
                  className={shell.className}
                  role='button'
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('.en-task')) return;
                    setEarnExpandedCat(isOpen ? null : cat);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      (e.currentTarget as HTMLDivElement).click();
                    }
                  }}
                >
                  <div className='en-cat-hdr'>
                    <div className='en-cat-hdr-l'>
                      <span className='en-cat-num'>{numLabel}</span>
                      <div className='en-cat-title-row'>
                        <span className='en-cat-tt'>{getDisplayCategoryName(cat)}</span>
                        <span className={shell.pillClassName}>
                          {completed}/{total} completed
                        </span>
                      </div>
                    </div>
                    <div className='en-cat-hdr-r'>
                      <div className='en-cat-bar' aria-hidden='true'>
                        <div
                          className='en-cat-bar-fill'
                          style={{ width: `${shell.progressPct}%` }}
                        />
                      </div>
                      <span className='en-cat-toggle' aria-hidden='true'>
                        +
                      </span>
                    </div>
                  </div>

                  {isOpen && (
                    <div className='en-cat-body'>
                      {filtered.length === 0 ? (
                        <div className='en-empty'>
                          {earnFilter === 'all'
                            ? 'No events in this category.'
                            : `No ${earnFilter} events in this category.`}
                        </div>
                      ) : (
                        filtered.map((evt) => {
                          const isNewsletterShell = evt.id === NEWSLETTER_SHELL_ID;
                          const shellLocked =
                            isNewsletterShell &&
                            (Boolean(evt.completed) || Boolean(newsletterSubscribedOn));
                          const shellClickable =
                            isNewsletterShell && !shellLocked && !newsletterStatusLoading;
                          const inAppReviewShell = evt.id === IN_APP_REVIEW_TASK_CODE && !evt.completed;
                          const socialShell = SOCIAL_TASK_CODES.includes(evt.id);
                          const shellInteractive = shellClickable || inAppReviewShell || socialShell;
                          const openShell = () => {
                            if (socialShell) {
                              navigate(SOCIAL_SUBMISSIONS_PATH);
                              return;
                            }
                            if (inAppReviewShell) {
                              setModal(IN_APP_REVIEW_MODAL_ID);
                              return;
                            }
                            if (!shellClickable) return;
                            setNewsletterError(null);
                            setNewsletterModalOpen(true);
                          };

                          return (
                            <div
                              key={evt.id}
                              role={shellInteractive ? 'button' : undefined}
                              tabIndex={shellInteractive ? 0 : undefined}
                              onClick={(e) => {
                                e.stopPropagation();
                                openShell();
                              }}
                              onKeyDown={
                                shellInteractive
                                  ? (e) => {
                                      e.stopPropagation();
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        openShell();
                                      }
                                    }
                                  : undefined
                              }
                              className={`en-task${evt.completed ? ' is-done' : ''}${
                                shellInteractive ? ' cursor-pointer' : ''
                              }`}
                            >
                              <div className='en-task-l'>
                                <span className='en-task-dot' aria-hidden='true' />
                                <div className='en-task-info'>
                                  <div className='en-task-title'>{evt.name}</div>
                                  <span className='en-task-freq'>{evt.limit}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <aside className='en-howto-split-r'>
          <EarnReferralSummary />
        </aside>
      </div>

      <NewsletterSignupModal
        open={newsletterModalOpen}
        onClose={() => {
          if (!newsletterSubmitting) {
            setNewsletterModalOpen(false);
            setNewsletterError(null);
          }
        }}
        onSubscribe={handleNewsletterSubscribe}
        submitting={newsletterSubmitting}
        errorText={newsletterError}
      />
    </div>
  );
};
